import React from 'react';
import { LangBlock } from '../../global';
import { SortType } from './usePaginatedTable';

export type AdminTableColumn<T> = {
    key: string;
    label: string;
    sortable?: boolean;
    primary?: boolean;
    hideMobile?: boolean;
    render: (row: T, index: number) => React.ReactNode;
};

type Props<T> = {
    columns: AdminTableColumn<T>[];
    data: T[];
    rowKey: (row: T, index: number) => string | number;
    sortColumn?: string;
    sortType?: SortType;
    onSort?: (column: string) => void;
    emptyMessage?: string;
};

export const AdminDataTable = <T,>({
    columns,
    data,
    rowKey,
    sortColumn,
    sortType,
    onSort,
    emptyMessage,
}: Props<T>) => {
    const [, , LangString] = LangBlock();
    const emptyText = emptyMessage ?? LangString('AdminNoData');
    const primaryColumn = columns.find((col) => col.primary) ?? columns[0];

    if (!data.length) {
        return <div className="admin-empty">{emptyText}</div>;
    }

    const sortIcon = (key: string) => {
        if (sortColumn !== key) {
            return '↕';
        }

        return sortType === 'asc' ? '↑' : '↓';
    };

    return (
        <div className="admin-data-table">
            <div className="admin-data-table__desktop">
                <div className="admin-data-table__scroll">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th key={column.key}>
                                        {column.sortable && onSort ? (
                                            <button
                                                type="button"
                                                className={`admin-table__sort${sortColumn === column.key ? ' admin-table__sort--active' : ''}`}
                                                onClick={() => onSort(column.key)}
                                            >
                                                <span>{column.label}</span>
                                                <span className="admin-table__sort-icon">
                                                    {sortIcon(column.key)}
                                                </span>
                                            </button>
                                        ) : (
                                            column.label
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, index) => (
                                <tr key={rowKey(row, index)}>
                                    {columns.map((column) => (
                                        <td key={column.key}>{column.render(row, index)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="admin-data-table__mobile">
                {data.map((row, index) => (
                    <article key={rowKey(row, index)} className="admin-table-card">
                        {primaryColumn ? (
                            <div className="admin-table-card__title">
                                {primaryColumn.render(row, index)}
                            </div>
                        ) : null}
                        <div className="admin-table-card__rows">
                            {columns
                                .filter((column) => column.key !== primaryColumn?.key)
                                .filter((column) => !column.hideMobile)
                                .map((column) => (
                                    <div key={column.key} className="admin-table-card__row">
                                        <span className="admin-table-card__label">
                                            {column.label}
                                        </span>
                                        <span className="admin-table-card__value">
                                            {column.render(row, index)}
                                        </span>
                                    </div>
                                ))}
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
};
